import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';

const Vehicle = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const recordsPerPage = 10;
  const mediaUrl = process.env.REACT_APP_MEDIA_URL;

  useEffect(() => {
    fetchVehicles();
  }, [currentPage, search, dateFilter]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await apiService.post('vehicles', {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: search,
        date: dateFilter
      });

      setVehicles(response.data || []);
      setTotalRecords(response.recordsFiltered || 0);
      setTotalPages(Math.ceil((response.recordsFiltered || 0) / recordsPerPage));
    } catch (error) {
      toast.error('Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (id) => {
    try {
      const response = await apiService.get(`vehicleStatus/${id}`);
      if (response.success) {
        toast.error(response.message);
      } else {
        toast.success('Vehicle Status updated successfully');
        fetchVehicles();
      }
    } catch (error) {
      toast.error('Something went wrong!!');
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await apiService.get(`deleteVehicle/${deleteId}`);
      if (response.success) {
        toast.error(response.message);
      } else {
        toast.success('Vehicle deleted successfully');
        setShowDeleteModal(false);
        fetchVehicles();
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  if (loading && currentPage === 1) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#009ef7' }}></div>
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    );
  }

  return (
    <div className="p-0">
      <div className="bg-white rounded-lg shadow">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="text-xl font-bold text-gray-900">Vehicles</h3>
          <button
            onClick={() => navigate('/vehicle/create')}
            className="px-2 sm:px-4 py-2 text-sm font-medium text-white rounded-lg flex items-center"
            style={{ backgroundColor: '#009ef7' }}
          >
            <i className="bi bi-plus text-lg mr-1"></i>
            Add New
          </button>
        </div>

        <div className="p-6">
          <div className="flex gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  placeholder="Search query here.."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                />
              </div>
            </div>
            <div className="w-64">
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }}
                className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-sm">
                  <th className="px-4 py-3 text-left font-semibold">ID</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[200px]">Image</th>
                  {user?.role === 'admin' && <th className="px-4 py-3 text-left font-semibold min-w-[200px]">Business</th>}
                  <th className="px-4 py-3 text-left font-semibold">Name</th>
                  <th className="px-4 py-3 text-left font-semibold">Charges/KM</th>
                  <th className="px-4 py-3 text-left font-semibold">Charges/Day</th>
                  <th className="px-4 py-3 text-left font-semibold">Live</th>
                  <th className="px-4 py-3 text-left font-semibold">Action</th>
                  <th className="px-4 py-3 text-left font-semibold">CreatedAt</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.length === 0 ? (
                  <tr>
                    <td colSpan={user?.role === 'admin' ? '9' : '8'} className="px-4 py-8 text-center text-gray-500">
                      No vehicles found
                    </td>
                  </tr>
                ) : (
                  vehicles.map((item, index) => (
                    <tr key={item._id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-900">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                      <td className="px-4 py-3">
                        <img
                          src={item.image ? `${mediaUrl}${item.image}` : '/placeholder.png'}
                          alt={item.name}
                          className="h-20 w-40 object-cover rounded"
                        />
                      </td>
                      {user?.role === 'admin' && (
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            {item.vendorId ? (
                              <>
                                <Link to={`/vendorWallet/${item.vendorId._id}`} className="text-sm text-gray-900 hover:text-[#009ef7] font-medium no-underline">
                                  {item.vendorId.business_name || '-'}
                                </Link>
                                {item.vendorId.slug && (
                                  <a
                                    href={`https://www.travellinglark.com/agent/${item.vendorId.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block px-2 py-1 text-[11px] font-semibold bg-cyan-100 text-cyan-800 rounded-md hover:bg-cyan-200 w-fit no-underline"
                                  >
                                    <i className="bi bi-building mr-1"></i>View Profile
                                  </a>
                                )}
                              </>
                            ) : (
                              <span className="text-gray-400">--</span>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-3 text-gray-900">{item.name}</td>
                      <td className="px-4 py-3 text-gray-900">{item.chargesKm}</td>
                      <td className="px-4 py-3 text-gray-900">{item.chargesDay}</td>
                      <td className="px-4 py-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={item.status === 1}
                            onChange={() => handleStatusToggle(item._id)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009ef7]"></div>
                        </label>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/vehicle/edit/${item._id}`)}
                            className="px-3 py-1 text-xs font-semibold rounded"
                            style={{ backgroundColor: '#d4f4dd', color: '#1bc943' }}
                          >
                            <i className="bi bi-pencil text-lg"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(item._id)}
                            className="px-3 py-1 text-xs font-semibold rounded"
                            style={{ backgroundColor: '#ffe2e5', color: '#f1416c' }}
                          >
                            <i className="bi bi-trash text-lg"></i>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {item.createdAt ? new Date(item.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '--'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalRecords > 0 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-600">
                Showing {(currentPage - 1) * recordsPerPage + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 text-sm border rounded ${
                      currentPage === page
                        ? 'text-white'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    style={currentPage === page ? { backgroundColor: '#009ef7', borderColor: '#009ef7' } : {}}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Confirmation</h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-700">Are you sure you want to delete this Vehicle?</p>
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicle;
